import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Table, Input, Select, Tooltip } from "antd";
import { all_routes } from "../../../router/all_routes";
import { getAllWillingness, getStaffQuarterLocations } from "../../../../core/data/redux/staffQuarterSlice";
import { toast } from "react-toastify";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";
import { formatFlatNoWithBlock, resolveFlatCategory } from "../staffQuarterUtils";

const { Option } = Select;

// Recursively unpack arrays from any response envelope
const extractRecords = (res: any): any[] => {
    if (!res) return [];
    if (Array.isArray(res)) return res;

    const candidates = [
        res?.data?.data,
        res?.data?.willingness,
        res?.data?.allWillingness,
        res?.data?.all_willingness,
        res?.data?.requests,
        res?.data?.rows,
        res?.data?.items,
        res?.data?.list,
        res?.data?.records,
        res?.data?.result,
        res?.data,
        res?.willingness,
        res?.allWillingness,
        res?.all_willingness,
        res?.requests,
        res?.rows,
        res?.items,
        res?.list,
        res?.records,
        res?.result,
        res?.Response?.data,
        res?.response?.data,
    ];

    for (const c of candidates) {
        if (Array.isArray(c)) return c;
    }

    if (typeof res === "object") {
        for (const val of Object.values(res)) {
            if (Array.isArray(val)) return val;
            if (val && typeof val === "object") {
                for (const subVal of Object.values(val as any)) {
                    if (Array.isArray(subVal)) return subVal;
                }
            }
        }
    }

    return [];
};

// Normalize a willingness record for robust field access
const normalizeRecord = (item: any, idx: number) => {
    const raw = item || {};
    const ref = raw.referenceNumber ||
        raw.willingnessReferenceNumber ||
        raw.reference_number ||
        raw.willingness_reference_number ||
        raw.refNo ||
        raw.willingnessRef ||
        raw.registrationReferenceNumber ||
        raw.registration_reference_number ||
        raw.id ||
        `REF-${idx + 1}`;

    const empName = raw.employeeName ||
        raw.employee_name ||
        raw.empName ||
        raw.emp_name ||
        raw.employee?.employeeName ||
        raw.employee?.name ||
        raw.registration?.employeeName ||
        raw.registrationDetails?.employeeName ||
        raw.name ||
        "-";

    const empNo = raw.employeeNumber ||
        raw.employee_number ||
        raw.empNo ||
        raw.emp_no ||
        raw.empId ||
        raw.employeeId ||
        raw.employee?.employeeNumber ||
        raw.employee?.empId ||
        raw.registration?.employeeNumber ||
        "-";

    const designation = raw.designation ||
        raw.employeeDesignation ||
        raw.designationName ||
        raw.designation_name ||
        raw.employee?.designation ||
        raw.registration?.designation ||
        "-";

    const department = raw.department ||
        raw.departmentName ||
        raw.department_name ||
        raw.dept ||
        raw.employee?.department ||
        raw.registration?.department ||
        "-";

    const flatNo = formatFlatNoWithBlock(raw, raw.block || raw.blockName || raw.flatDetails?.block || raw.inventoryDetails?.block || raw.inventory?.block, raw);

    // Location Preference extraction
    const rawLocPref = raw.locationPreference ||
        raw.location_preference ||
        raw.locationPreferences ||
        raw.preferredLocations ||
        raw.location_preferences ||
        raw.registration?.locationPreference ||
        raw.registrationDetails?.locationPreference;

    let locPrefList: string[] = [];
    if (Array.isArray(rawLocPref)) {
        rawLocPref.forEach((lp: any) => {
            if (typeof lp === "string" && lp.trim()) {
                locPrefList.push(lp.trim());
            } else if (lp && typeof lp === "object") {
                const locName = lp.locationName || lp.name || lp.location?.name || lp.location || lp.preferredLocation || lp.location_name;
                if (locName && typeof locName === "string") {
                    locPrefList.push(locName.trim());
                }
            }
        });
    } else if (typeof rawLocPref === "string" && rawLocPref.trim()) {
        try {
            const parsed = JSON.parse(rawLocPref);
            if (Array.isArray(parsed)) {
                parsed.forEach((p: any) => {
                    if (typeof p === "string") locPrefList.push(p.trim());
                    else if (p?.name || p?.locationName || p?.location) locPrefList.push((p.name || p.locationName || p.location).trim());
                });
            } else if (typeof parsed === "object" && parsed !== null) {
                Object.values(parsed).forEach((v: any) => {
                    if (typeof v === "string") locPrefList.push(v.trim());
                });
            } else {
                locPrefList = [rawLocPref.trim()];
            }
        } catch {
            locPrefList = rawLocPref.split(",").map((s) => s.trim()).filter(Boolean);
        }
    } else if (typeof rawLocPref === "object" && rawLocPref !== null) {
        Object.values(rawLocPref).forEach((v: any) => {
            if (typeof v === "string") locPrefList.push(v.trim());
            else if (v?.name || v?.locationName || v?.location) locPrefList.push((v.name || v.locationName || v.location).trim());
        });
    }

    const locationRaw = (typeof raw.location === "string" ? raw.location : raw.location?.name) ||
        raw.locationName ||
        raw.location_name ||
        (typeof raw.flatDetails?.location === "string" ? raw.flatDetails?.location : raw.flatDetails?.location?.name) ||
        raw.flatDetails?.locationName ||
        raw.flatDetails?.location_name ||
        (typeof raw.flat?.location === "string" ? raw.flat?.location : raw.flat?.location?.name) ||
        raw.flat?.locationName ||
        raw.flat?.location_name ||
        (typeof raw.inventoryDetails?.location === "string" ? raw.inventoryDetails?.location : raw.inventoryDetails?.location?.name) ||
        raw.inventoryDetails?.locationName ||
        raw.inventoryDetails?.location_name ||
        (typeof raw.inventory?.location === "string" ? raw.inventory?.location : raw.inventory?.location?.name) ||
        raw.inventory?.locationName ||
        raw.inventory?.location_name ||
        (typeof raw.registration?.location === "string" ? raw.registration?.location : raw.registration?.location?.name) ||
        raw.registration?.locationName ||
        raw.registration?.location_name ||
        raw.registrationDetails?.location ||
        raw.registrationDetails?.locationName ||
        raw.registrationDetails?.location_name ||
        "";

    let location = (typeof locationRaw === "string" && locationRaw.trim()) ? locationRaw.trim() : "";
    if (!location && locPrefList.length > 0) {
        location = locPrefList.join(", ");
    }
    if (!location) {
        location = "-";
    }

    const quarterTypeRaw =
        (typeof raw.flatDetails?.quarterType === "string" ? raw.flatDetails?.quarterType : raw.flatDetails?.quarterType?.name) ||
        raw.flatDetails?.quarterTypeName ||
        raw.flatDetails?.quarter_type ||
        raw.flatDetails?.flatType ||
        raw.flatDetails?.type ||
        (typeof raw.flat?.quarterType === "string" ? raw.flat?.quarterType : raw.flat?.quarterType?.name) ||
        raw.flat?.flatType ||
        raw.inventoryDetails?.quarterType ||
        raw.inventoryDetails?.flatType ||
        raw.inventory?.quarterType ||
        raw.quarterType?.name ||
        raw.quarterTypeName ||
        raw.quarter_type ||
        (typeof raw.quarterType === "string" ? raw.quarterType : "") ||
        raw.flatType ||
        raw.registration?.quarterTypeEligibility?.quarterType ||
        raw.registrationDetails?.quarterTypeEligibility?.quarterType ||
        raw.quarterTypeEligibility?.quarterType ||
        "-";

    const quarterType = (typeof quarterTypeRaw === "string" && quarterTypeRaw.trim()) ? quarterTypeRaw.trim() : "-";

    const poolRaw =
        (typeof raw.flatDetails?.poolType === "string" ? raw.flatDetails?.poolType : raw.flatDetails?.poolType?.name) ||
        raw.flatDetails?.poolName ||
        raw.flatDetails?.pool_name ||
        raw.flatDetails?.pool ||
        (typeof raw.flat?.pool === "string" ? raw.flat?.pool : raw.flat?.pool?.name) ||
        raw.inventoryDetails?.pool ||
        raw.inventory?.pool ||
        raw.pool?.name ||
        raw.poolName ||
        raw.pool_name ||
        raw.poolType?.name ||
        (typeof raw.pool === "string" ? raw.pool : "") ||
        "-";

    const pool = (typeof poolRaw === "string" && poolRaw.trim()) ? poolRaw.trim() : "-";

    const invitationDate = raw.invitationDate ||
        raw.invitation_date ||
        raw.invitedAt ||
        raw.invited_at ||
        raw.createdAt ||
        raw.created_at ||
        raw.date ||
        null;

    const expiryDate = raw.expiryDate ||
        raw.expiry_date ||
        raw.expiresAt ||
        raw.expires_at ||
        raw.validTill ||
        raw.valid_till ||
        null;

    const status = (raw.willingnessStatus || raw.willingness_status || raw.status || raw.state || "PENDING").toString().toUpperCase();

    const regId = raw.registrationId || raw.registration_id || raw.registration?.id || raw.id;

    const category = resolveFlatCategory(raw, raw.block || raw.flatDetails?.block, raw);

    return {
        ...raw,
        _raw: raw,
        _key: raw.id ? `w-${raw.id}` : `idx-${idx}-${ref}`,
        _ref: ref,
        _empName: empName,
        _empNo: empNo,
        _designation: designation,
        _department: department,
        _flatNo: flatNo,
        _category: category,
        _location: location,
        _locationPreference: locPrefList,
        _quarterType: quarterType,
        _pool: pool,
        _invitationDate: invitationDate,
        _expiryDate: expiryDate,
        _status: status,
        _regId: regId
    };
};

const WillingnessInvitedReport: React.FC = () => {
    const routes = all_routes as any;
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [apiLocations, setApiLocations] = useState<any[]>([]);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [quarterTypeFilter, setQuarterTypeFilter] = useState("ALL");
    const [locationFilter, setLocationFilter] = useState("ALL");
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    const fetchLocations = async () => {
        try {
            const res = await dispatch(getStaffQuarterLocations({})).unwrap();
            let locs: any[] = [];
            if (Array.isArray(res)) {
                locs = res;
            } else if (Array.isArray(res?.data)) {
                locs = res.data;
            } else if (Array.isArray(res?.data?.data)) {
                locs = res.data.data;
            }
            setApiLocations(locs);
        } catch (e) {
            console.error("Failed to load locations from location API:", e);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await dispatch(getAllWillingness({ page: 1, limit: 200 })).unwrap();
            console.log("all-willingness response:", res);
            const rawRecords = extractRecords(res);
            const normalized = rawRecords.map((r, i) => normalizeRecord(r, i));
            setData(normalized);
        } catch (error: any) {
            console.error("Failed to load willingness invitations:", error);
            toast.error(typeof error === "string" ? error : "Failed to load willingness invitations");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLocations();
        fetchData();
    }, [dispatch]);

    const formatDateTime = (date: any) => {
        if (!date || date === "-") return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return String(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString("default", { month: "short" });
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");

        return (
            <div className="d-flex flex-column">
                <span className="text-dark fw-medium" style={{ fontSize: "13px" }}>
                    <i className="ti ti-calendar-event me-1 text-muted"></i>
                    {`${day} ${month}, ${year}`}
                </span>
                <span className="text-muted extra-small" style={{ fontSize: "11px" }}>
                    <i className="ti ti-clock me-1"></i>
                    {`${hours}:${minutes}`}
                </span>
            </div>
        );
    };

    // Extract unique Quarter Types for filter dropdown
    const uniqueQuarterTypes = useMemo(() => {
        const set = new Set<string>();
        data.forEach((item: any) => {
            if (item._quarterType && item._quarterType !== "-") {
                set.add(item._quarterType);
            }
        });
        return Array.from(set);
    }, [data]);

    // Build unified location list from Location API + willingness records
    const unifiedLocations = useMemo(() => {
        const locMap = new Map<string, string>(); // lowercase key -> display label

        // 1. From Location API
        apiLocations.forEach((l: any) => {
            const name = typeof l === "string" ? l : (l.name || l.locationName || l.label);
            if (name && typeof name === "string" && name.trim()) {
                locMap.set(name.trim().toLowerCase(), name.trim());
            }
        });

        // 2. From assigned location in data
        data.forEach((item: any) => {
            if (item._location && item._location !== "-") {
                locMap.set(item._location.trim().toLowerCase(), item._location.trim());
            }
            if (Array.isArray(item._locationPreference)) {
                item._locationPreference.forEach((p: string) => {
                    if (p && p.trim()) {
                        locMap.set(p.trim().toLowerCase(), p.trim());
                    }
                });
            }
        });

        return Array.from(locMap.values()).sort((a, b) => a.localeCompare(b));
    }, [apiLocations, data]);

    // Statistics counts
    const stats = useMemo(() => {
        const total = data.length;
        let accepted = 0;
        let pending = 0;
        let declined = 0;
        let expired = 0;

        data.forEach((item: any) => {
            const st = item._status;
            if (st === "ACCEPTED" || st === "APPROVED" || st === "ALLOTTED" || st === "ALLOTED") {
                accepted++;
            } else if (st === "DECLINED" || st === "REJECTED") {
                declined++;
            } else if (st === "EXPIRED") {
                expired++;
            } else {
                pending++;
            }
        });

        return { total, accepted, pending, declined, expired };
    }, [data]);

    // Filter data
    const filteredData = useMemo(() => {
        return data.filter((item: any) => {
            if (statusFilter !== "ALL") {
                if (statusFilter === "ACCEPTED" && !["ACCEPTED", "APPROVED", "ALLOTTED", "ALLOTED"].includes(item._status)) {
                    return false;
                } else if (statusFilter === "DECLINED" && !["DECLINED", "REJECTED"].includes(item._status)) {
                    return false;
                } else if (statusFilter === "PENDING" && !["PENDING", "SUBMITTED", "UNDER REVIEW", "NOT_ALLOTTED", "NOT ALLOTTED"].includes(item._status)) {
                    return false;
                } else if (statusFilter === "EXPIRED" && item._status !== "EXPIRED") {
                    return false;
                }
            }

            if (quarterTypeFilter !== "ALL" && item._quarterType.toLowerCase() !== quarterTypeFilter.toLowerCase()) {
                return false;
            }

            if (locationFilter !== "ALL") {
                const target = locationFilter.toLowerCase();
                const matchesAssigned = item._location && item._location.toLowerCase().includes(target);
                const matchesPref = Array.isArray(item._locationPreference) &&
                    item._locationPreference.some((p: string) => p.toLowerCase().includes(target));

                if (!matchesAssigned && !matchesPref) {
                    return false;
                }
            }

            if (searchText.trim()) {
                const term = searchText.toLowerCase();
                const ref = item._ref.toLowerCase();
                const name = item._empName.toLowerCase();
                const empNo = item._empNo.toLowerCase();
                const flat = item._flatNo.toLowerCase();
                const location = item._location.toLowerCase();
                const dept = item._department.toLowerCase();
                const prefs = (item._locationPreference || []).join(" ").toLowerCase();

                return ref.includes(term) ||
                    name.includes(term) ||
                    empNo.includes(term) ||
                    flat.includes(term) ||
                    location.includes(term) ||
                    dept.includes(term) ||
                    prefs.includes(term);
            }

            return true;
        });
    }, [data, statusFilter, quarterTypeFilter, locationFilter, searchText]);

    // Group filtered records by Employee (for employees with multiple willingness invitations)
    const groupedData = useMemo(() => {
        if (!Array.isArray(filteredData) || filteredData.length === 0) return [];

        const groups: { [key: string]: any } = {};

        filteredData.forEach((item: any) => {
            const empIdentifier = (item._empNo && item._empNo !== "-")
                ? item._empNo
                : (item._regId ? `reg-${item._regId}` : item._empName);
            const groupKey = String(empIdentifier);

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    ...item,
                    _key: `grp-${groupKey}`,
                    willingnessList: [item],
                    totalOpportunities: 1,
                };
            } else {
                const existing = groups[groupKey];
                existing.willingnessList.push(item);
                existing.totalOpportunities += 1;
            }
        });

        return Object.values(groups);
    }, [filteredData]);

    const handleExportCSV = () => {
        if (filteredData.length === 0) {
            toast.warning("No data to export.");
            return;
        }

        const escapeCsv = (str: any) => {
            if (str === null || str === undefined) return '""';
            const clean = String(str).replace(/"/g, '""');
            return `"${clean}"`;
        };

        const now = new Date();
        const dateStr = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

        const metadataHeader = [
            `"===================================================================================================="`,
            `"DELHI METRO RAIL CORPORATION LIMITED - STAFF QUARTER MANAGEMENT"`,
            `"WILLINGNESS REPORT"`,
            `"Generated On: ${dateStr} | Total Filtered Records: ${filteredData.length}"`,
            `"===================================================================================================="`,
            `""`
        ];

        const headers = [
            "S.No",
            "Willingness Ref",
            "Employee ID",
            "Employee Name",
            "Designation",
            "Department",
            "Quarter Type",
            "Pool",
            "Flat Number",
            "Assigned Location",
            "Location Preference",
            "Status",
            "Invitation Date",
            "Expiry Date"
        ].map(escapeCsv).join(",");

        const rows = filteredData.map((item: any, idx: number) => {
            const prefLocs = Array.isArray(item._locationPreference)
                ? item._locationPreference.join(", ")
                : (item._locationPreference || "-");

            return [
                escapeCsv(idx + 1),
                escapeCsv(item._ref || "-"),
                escapeCsv(item._empNo || "-"),
                escapeCsv(item._empName || "-"),
                escapeCsv(item._designation || "-"),
                escapeCsv(item._department || "-"),
                escapeCsv(item._quarterType || "-"),
                escapeCsv(item._pool || "-"),
                escapeCsv(item._flatNo || "-"),
                escapeCsv(item._location || "-"),
                escapeCsv(prefLocs),
                escapeCsv(item._status || "-"),
                escapeCsv(item._invitationDate || "-"),
                escapeCsv(item._expiryDate || "-")
            ].join(",");
        });

        const summaryFooter = [
            `""`,
            `"===================================================================================================="`,
            `"SUMMARY STATISTICS"`,
            `"Total Invitations","Accepted / Allotted","Pending / Not Allotted","Declined / Rejected","Expired"`,
            `"${stats.total}","${stats.accepted}","${stats.pending}","${stats.declined}","${stats.expired}"`,
            `"===================================================================================================="`
        ];

        const fullCsvString = "\uFEFF" + [
            ...metadataHeader,
            headers,
            ...rows,
            ...summaryFooter
        ].join("\n");

        const blob = new Blob([fullCsvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Willingness_Report_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success("Willingness Report exported successfully!");
    };

    const columns = [
        {
            title: "Willingness Ref",
            dataIndex: "_ref",
            key: "_ref",
            render: (_: string, record: any) => {
                const list = record.willingnessList || [];
                if (list.length > 1) {
                    return (
                        <div className="d-flex flex-column gap-1">
                            <span className="fw-semibold text-primary" style={{ fontSize: "13px" }}>
                                {list[0]._ref}
                            </span>
                            <span className="badge bg-primary-light text-primary" style={{ width: "fit-content", fontSize: "10px" }}>
                                +{list.length - 1} more
                            </span>
                        </div>
                    );
                }
                return (
                    <span className="fw-semibold text-primary" style={{ fontSize: "13px" }}>
                        {record._ref}
                    </span>
                );
            },
            sorter: (a: any, b: any) => (a._ref || "").localeCompare(b._ref || ""),
        },
        {
            title: "Employee",
            dataIndex: "_empName",
            key: "_empName",
            render: (_: any, record: any) => {
                const name = record._empName !== "-" ? record._empName : "Employee";
                const empNum = record._empNo;
                const count = record.willingnessList?.length || 1;
                return (
                    <div className="d-flex align-items-center">
                        <span
                            className="avatar-title rounded-circle me-2 fw-bold text-white flex-shrink-0"
                            style={{
                                width: "34px",
                                height: "34px",
                                background: "#C0282C",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                            }}
                        >
                            {name?.charAt(0)?.toUpperCase()}
                        </span>
                        <div>
                            <div className="d-flex align-items-center gap-1">
                                <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "13.5px" }}>
                                    {name}
                                </h6>
                                {count > 1 && (
                                    <span className="badge bg-primary-light text-primary" style={{ fontSize: "10px", padding: "2px 6px" }}>
                                        {count} Requests
                                    </span>
                                )}
                            </div>
                            <span className="text-muted small">Emp ID: {empNum}</span>
                        </div>
                    </div>
                );
            },
            sorter: (a: any, b: any) => (a._empName || "").localeCompare(b._empName || ""),
        },
        {
            title: "Designation & Dept",
            key: "designation_dept",
            render: (_: any, record: any) => (
                <div>
                    <div className="text-dark fw-medium" style={{ fontSize: "13px" }}>
                        {record._designation}
                    </div>
                    <small className="text-muted">{record._department}</small>
                </div>
            ),
        },
        {
            title: "Flat & Location",
            key: "flat_loc",
            render: (_: any, record: any) => {
                const list = record.willingnessList || [];
                if (list.length > 1) {
                    const flats: any[] = Array.from(new Set(list.map((w: any) => w._flatNo).filter((f: any) => f && f !== "-")));
                    const locs: any[] = Array.from(new Set(list.flatMap((w: any) => {
                        const l = w._location !== "-" ? w._location : "";
                        const prefs = Array.isArray(w._locationPreference) ? w._locationPreference : [];
                        return [l, ...prefs].filter((loc: any) => loc && typeof loc === "string" && loc.trim() && loc !== "-");
                    })));

                    return (
                        <div>
                            {flats.length > 0 && (
                                <div className="d-flex flex-wrap gap-1 mb-1">
                                    {flats.map((f: any, idx: number) => (
                                        <span key={idx} className="badge bg-light text-dark fw-bold border">
                                            Flat {String(f)}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {locs.length > 0 && (
                                <div className="small text-muted">
                                    <i className="ti ti-map-pin me-1 text-muted"></i>
                                    {locs.join(", ")}
                                </div>
                            )}
                            {flats.length === 0 && locs.length === 0 && (
                                <span className="text-muted small">-</span>
                            )}
                        </div>
                    );
                }

                const flatNo = record._flatNo !== "-" ? record._flatNo : null;
                const loc = record._location !== "-" ? record._location : null;
                const prefs: string[] = record._locationPreference || [];

                return (
                    <div>
                        {flatNo && (
                            <div className="mb-1">
                                <span className="badge bg-light text-dark fw-bold border me-1">
                                    Flat {flatNo}
                                </span>
                                {loc && (
                                    <span className="small text-muted">
                                        <i className="ti ti-map-pin me-1 text-muted"></i>{loc}
                                    </span>
                                )}
                            </div>
                        )}
                        {!flatNo && loc && (
                            <div className="small text-dark fw-medium mb-1">
                                <i className="ti ti-map-pin text-primary me-1"></i>{loc}
                            </div>
                        )}
                        {prefs.length > 0 && (
                            <div className="d-flex flex-column gap-1 mt-1">
                                <span className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>
                                    <i className="ti ti-map-pin me-1 text-primary"></i>Location:
                                </span>
                                <div className="d-flex flex-wrap gap-1">
                                    {prefs.map((pref, i) => (
                                        <span
                                            key={i}
                                            className="badge bg-light text-primary border"
                                            style={{ fontSize: "11px", padding: "2px 6px" }}
                                        >
                                            {prefs.length > 1 ? `${i + 1}. ` : ""}{pref}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {!flatNo && !loc && prefs.length === 0 && (
                            <span className="text-muted small">-</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Quarter Type & Pool",
            key: "type_pool",
            render: (_: any, record: any) => {
                const list = record.willingnessList || [];
                if (list.length > 1) {
                    const types: any[] = Array.from(new Set(list.map((w: any) => w._quarterType).filter((t: any) => t && t !== "-")));
                    return (
                        <div>
                            {types.map((qType: any, idx: number) => {
                                const qStr = String(qType);
                                const displayType = qStr.toLowerCase().startsWith("type") || qStr.toLowerCase().startsWith("quarter")
                                    ? qStr
                                    : `Type ${qStr}`;
                                return (
                                    <span key={idx} className="badge bg-primary-light text-primary me-1 mb-1">
                                        {displayType}
                                    </span>
                                );
                            })}
                            <div className="small text-muted">{record._pool}</div>
                        </div>
                    );
                }

                const qType = record._quarterType || "-";
                const displayType = qType === "-"
                    ? "-"
                    : qType.toLowerCase().startsWith("type") || qType.toLowerCase().startsWith("quarter")
                        ? qType
                        : `Type ${qType}`;

                return (
                    <div>
                        <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-primary-light text-primary">
                                {displayType}
                            </span>
                            <span className="badge bg-light text-dark border" style={{ fontSize: "11px" }}>
                                {record._category || "UR"}
                            </span>
                        </div>
                        <div className="small text-muted">{record._pool}</div>
                    </div>
                );
            },
        },
        {
            title: "Invitation Date",
            dataIndex: "_invitationDate",
            key: "_invitationDate",
            render: (text: any) => formatDateTime(text),
            sorter: (a: any, b: any) => {
                const aDate = new Date(a._invitationDate || 0).getTime();
                const bDate = new Date(b._invitationDate || 0).getTime();
                return aDate - bDate;
            },
        },
        {
            title: "Status",
            dataIndex: "_status",
            key: "_status",
            render: (s: string, record: any) => {
                const list = record.willingnessList || [];
                if (list.length > 1) {
                    const statuses = list.map((w: any) => (w._status || "").toUpperCase());
                    if (statuses.some((st: string) => ["ACCEPTED", "APPROVED", "ALLOTTED", "ALLOTED"].includes(st))) {
                        return (
                            <span className="badge bg-success text-white px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1">
                                <i className="ti ti-check" style={{ fontSize: "12px" }}></i>
                                ALLOTTED
                            </span>
                        );
                    }
                    if (statuses.every((st: string) => ["DECLINED", "REJECTED"].includes(st))) {
                        return (
                            <span className="badge bg-danger text-white px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1">
                                <i className="ti ti-x" style={{ fontSize: "12px" }}></i>
                                DECLINED
                            </span>
                        );
                    }
                    if (statuses.some((st: string) => ["NOT_ALLOTTED", "NOT ALLOTTED"].includes(st))) {
                        return (
                            <span className="badge bg-warning text-dark px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1">
                                <i className="ti ti-clock" style={{ fontSize: "12px" }}></i>
                                NOT_ALLOTTED ({list.length})
                            </span>
                        );
                    }
                    return (
                        <span className="badge bg-warning text-dark px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1">
                            <i className="ti ti-clock" style={{ fontSize: "12px" }}></i>
                            PENDING ({list.length})
                        </span>
                    );
                }

                let bg = "bg-warning text-dark";
                let icon = "ti-clock";

                if (s === "ACCEPTED" || s === "APPROVED" || s === "ALLOTTED" || s === "ALLOTED") {
                    bg = "bg-success text-white";
                    icon = "ti-check";
                } else if (s === "DECLINED" || s === "REJECTED") {
                    bg = "bg-danger text-white";
                    icon = "ti-x";
                } else if (s === "EXPIRED") {
                    bg = "bg-secondary text-white";
                    icon = "ti-alert-circle";
                }

                return (
                    <span className={`badge ${bg} px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1`}>
                        <i className={`ti ${icon}`} style={{ fontSize: "12px" }}></i>
                        {s}
                    </span>
                );
            },
            sorter: (a: any, b: any) => (a._status || "").localeCompare(b._status || ""),
        },
        {
            title: "Action",
            key: "action",
            align: "center" as const,
            render: (_: any, record: any) => {
                const list = record.willingnessList || [];
                const isMultiple = list.length > 1;
                const key = record._key;
                const isExpanded = expandedRowKeys.includes(key);

                if (isMultiple) {
                    return (
                        <button
                            onClick={() => {
                                if (isExpanded) {
                                    setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
                                } else {
                                    setExpandedRowKeys([...expandedRowKeys, key]);
                                }
                            }}
                            className={`btn btn-sm ${isExpanded ? "btn-danger" : "btn-primary"} d-inline-flex align-items-center gap-1`}
                        >
                            <i className={`ti ${isExpanded ? "ti-chevron-up" : "ti-eye"}`}></i>
                            {isExpanded ? "Collapse" : "View"}
                        </button>
                    );
                }

                return (
                    <button
                        onClick={() => {
                            const regId = record._regId || record.registrationId || record.id;
                            const path = routes.staffQuarterAdmin.requestDetails.replace(":id", encodeRouteParam(regId));
                            const query = `?willingnessId=${record.id || ""}&willingnessRef=${record._ref || ""}`;
                            navigate(`${path}${query}`);
                        }}
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                    >
                        <i className="ti ti-eye"></i> View
                    </button>
                );
            },
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid pb-5">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold mb-1">
                                Willingness Invited Report
                            </h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarterAdmin.dashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarterAdmin.reports}>Reports</Link>
                                </li>
                                <li className="breadcrumb-item active text-primary fw-medium">
                                    Willingness Invited
                                </li>
                            </ul>
                        </div>
                        <div className="col-auto d-flex gap-2">
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-xs"
                                onClick={() => navigate(routes.staffQuarterAdmin.reports)}
                            >
                                <i className="ti ti-arrow-left"></i> Back to Reports
                            </button>
                            <button
                                className="btn btn-outline-primary d-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-xs"
                                onClick={() => {
                                    fetchLocations();
                                    fetchData();
                                }}
                                disabled={loading}
                                style={{ fontWeight: 600 }}
                            >
                                <i className={`ti ti-refresh ${loading ? "fa-spin" : ""}`} />
                                <span>Refresh</span>
                            </button>
                            <button
                                className="btn btn-primary d-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-xs"
                                onClick={handleExportCSV}
                                style={{ fontWeight: 600 }}
                            >
                                <i className="ti ti-download" />
                                <span>Export CSV</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quick Stats Cards */}
                <div className="row g-3 mb-4">
                    <div className="col-md-3 col-sm-6">
                        <div className="card border-0 shadow-sm rounded-3 p-3 h-100 bg-white">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-muted small fw-semibold text-uppercase">Total Invitations</span>
                                    <h4 className="fw-bold text-dark mt-1 mb-0">{stats.total}</h4>
                                </div>
                                <div className="rounded-3 p-2 bg-primary-light text-primary">
                                    <i className="ti ti-mail-forward fs-3"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-6">
                        <div className="card border-0 shadow-sm rounded-3 p-3 h-100 bg-white">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-muted small fw-semibold text-uppercase">Accepted / Allotted</span>
                                    <h4 className="fw-bold text-success mt-1 mb-0">{stats.accepted}</h4>
                                </div>
                                <div className="rounded-3 p-2 bg-success-light text-success">
                                    <i className="ti ti-circle-check fs-3"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-6">
                        <div className="card border-0 shadow-sm rounded-3 p-3 h-100 bg-white">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-muted small fw-semibold text-uppercase">Pending Response</span>
                                    <h4 className="fw-bold text-warning mt-1 mb-0">{stats.pending}</h4>
                                </div>
                                <div className="rounded-3 p-2 bg-warning-light text-warning">
                                    <i className="ti ti-clock fs-3"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-sm-6">
                        <div className="card border-0 shadow-sm rounded-3 p-3 h-100 bg-white">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-muted small fw-semibold text-uppercase">Declined / Expired</span>
                                    <h4 className="fw-bold text-danger mt-1 mb-0">{stats.declined + stats.expired}</h4>
                                </div>
                                <div className="rounded-3 p-2 bg-danger-light text-danger">
                                    <i className="ti ti-circle-x fs-3"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter and Table Card */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
                    <div className="card-header bg-white border-bottom p-3">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-4">
                                <Input
                                    prefix={<i className="ti ti-search text-muted me-1"></i>}
                                    placeholder="Search by Employee, Ref, Flat, Location or Dept..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    allowClear
                                    size="large"
                                    style={{ borderRadius: "8px" }}
                                />
                            </div>
                            <div className="col-md-3">
                                <Select
                                    value={statusFilter}
                                    onChange={(val) => setStatusFilter(val)}
                                    size="large"
                                    style={{ width: "100%" }}
                                >
                                    <Option value="ALL">All Statuses</Option>
                                    <Option value="PENDING">Pending</Option>
                                    <Option value="ACCEPTED">Accepted / Allotted</Option>
                                    <Option value="DECLINED">Declined / Rejected</Option>
                                    <Option value="EXPIRED">Expired</Option>
                                </Select>
                            </div>
                            <div className="col-md-2">
                                <Select
                                    value={quarterTypeFilter}
                                    onChange={(val) => setQuarterTypeFilter(val)}
                                    size="large"
                                    style={{ width: "100%" }}
                                >
                                    <Option value="ALL">All Types</Option>
                                    {uniqueQuarterTypes.map((t) => (
                                        <Option key={t} value={t}>
                                            {t.toLowerCase().startsWith("type") || t.toLowerCase().startsWith("quarter") ? t : `Type ${t}`}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="col-md-3">
                                <Select
                                    value={locationFilter}
                                    onChange={(val) => setLocationFilter(val)}
                                    size="large"
                                    style={{ width: "100%" }}
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Filter by Location"
                                >
                                    <Option value="ALL">All Locations</Option>
                                    {unifiedLocations.map((loc) => (
                                        <Option key={loc} value={loc}>
                                            {loc}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        <Table
                            columns={columns}
                            dataSource={groupedData}
                            rowKey={(record) => record._key || record.id || Math.random()}
                            loading={loading}
                            expandable={{
                                expandedRowKeys: expandedRowKeys,
                                onExpand: (expanded, record) => {
                                    const key = record._key;
                                    setExpandedRowKeys(expanded ? [key] : []);
                                },
                                expandedRowRender: (record: any) => {
                                    const nestedWillingness = record.willingnessList || [];
                                    return (
                                        <div className="p-3 bg-light rounded-3 my-2 border">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>
                                                    <i className="ti ti-home-star me-1 text-primary"></i>
                                                    Willingness Opportunities for {record._empName !== "-" ? record._empName : "Employee"} ({nestedWillingness.length} requests)
                                                </span>
                                            </div>
                                            <Table
                                                size="small"
                                                pagination={false}
                                                rowKey={(w: any) => w._key || w.id || w._ref}
                                                dataSource={nestedWillingness}
                                                columns={[
                                                    {
                                                        title: "Willingness Ref",
                                                        dataIndex: "_ref",
                                                        key: "_ref",
                                                        render: (text: string, w: any) => (
                                                            <span className="fw-bold text-primary" style={{ fontSize: "13px" }}>
                                                                {w._ref || text || "-"}
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        title: "Flat No",
                                                        dataIndex: "_flatNo",
                                                        key: "_flatNo",
                                                        render: (_: any, w: any) => {
                                                            const f = w._flatNo !== "-" ? w._flatNo : null;
                                                            return f ? (
                                                                <span className="badge bg-white text-dark border px-2 py-1 fw-semibold">
                                                                    {f}
                                                                </span>
                                                            ) : "-";
                                                        }
                                                    },
                                                    {
                                                        title: "Location",
                                                        dataIndex: "_location",
                                                        key: "_location",
                                                        render: (_: any, w: any) => {
                                                            const displayLoc = w._location !== "-"
                                                                ? w._location
                                                                : (Array.isArray(w._locationPreference) && w._locationPreference.length > 0
                                                                    ? w._locationPreference.join(", ")
                                                                    : "-");
                                                            return (
                                                                <span className="text-secondary fw-medium">
                                                                    {displayLoc}
                                                                </span>
                                                            );
                                                        }
                                                    },
                                                    {
                                                        title: "Quarter Type",
                                                        dataIndex: "_quarterType",
                                                        key: "_quarterType",
                                                        render: (_: any, w: any) => {
                                                            const qType = w._quarterType || "-";
                                                            return qType === "-" ? "-" : qType.toLowerCase().startsWith("type") || qType.toLowerCase().startsWith("quarter") ? qType : `Type ${qType}`;
                                                        }
                                                    },
                                                    {
                                                        title: "Invitation Date",
                                                        dataIndex: "_invitationDate",
                                                        key: "_invitationDate",
                                                        render: (_: any, w: any) => formatDateTime(w._invitationDate)
                                                    },
                                                    {
                                                        title: "Status",
                                                        dataIndex: "_status",
                                                        key: "_status",
                                                        render: (s: string) => {
                                                            let bg = "bg-warning text-dark";
                                                            let icon = "ti-clock";

                                                            if (s === "ACCEPTED" || s === "APPROVED" || s === "ALLOTTED" || s === "ALLOTED") {
                                                                bg = "bg-success text-white";
                                                                icon = "ti-check";
                                                            } else if (s === "DECLINED" || s === "REJECTED") {
                                                                bg = "bg-danger text-white";
                                                                icon = "ti-x";
                                                            } else if (s === "EXPIRED") {
                                                                bg = "bg-secondary text-white";
                                                                icon = "ti-alert-circle";
                                                            }

                                                            return (
                                                                <span className={`badge ${bg} px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1`}>
                                                                    <i className={`ti ${icon}`} style={{ fontSize: "12px" }}></i>
                                                                    {s}
                                                                </span>
                                                            );
                                                        }
                                                    },
                                                    {
                                                        title: "Action",
                                                        key: "action",
                                                        align: "center" as const,
                                                        render: (_: any, w: any) => (
                                                            <button
                                                                onClick={() => {
                                                                    const regId = w._regId || w.registrationId || w.id;
                                                                    const path = routes.staffQuarterAdmin.requestDetails.replace(":id", encodeRouteParam(regId));
                                                                    const query = `?willingnessId=${w.id || ""}&willingnessRef=${w._ref || ""}`;
                                                                    navigate(`${path}${query}`);
                                                                }}
                                                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                                            >
                                                                <i className="ti ti-eye"></i> View
                                                            </button>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </div>
                                    );
                                },
                                rowExpandable: (record: any) => Array.isArray(record.willingnessList) && record.willingnessList.length > 1,
                            }}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "25", "50", "100"],
                                showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                            }}
                            className="custom-table"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WillingnessInvitedReport;
